import { useState, useRef, useEffect } from "react";
import {
  InputRef,
  Input,
  Button,
  Space,
  DatePicker,
  Typography,
  Select,
  message,
  MenuProps,
  Dropdown,
  Popconfirm,
} from "antd";
import { SearchOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import lang from "src/lang";
import Export from "src/common/components/Export";
import apiRequest from "src/common/utils/apiRequest";
import coreFunctions from "src/common/utils/coreFunctions";
import { AppointmentStatus, Appointment } from "src/modules/appointment/types";
import { useStatusStore } from "../StatusStore";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const AppointmentTable = ({ actionRef }: { actionRef: any }) => {
  const { statuses } = useStatusStore();
  const [pageSize, setPageSize] = useState(15);
  const searchInput = useRef<InputRef>(null);
  const [tableData, setTableData] = useState<Appointment[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState<
    { key: string; value: any; type: string }[]
  >([]);
  const [searchAttributes, setSearchAttributes] = useState<
    { key: string; value: string }[]
  >([]);
  const [allSearch, setAllSearch] = useState<string>("");

  const [statusFilter, setStatusFilter] = useState<AppointmentStatus[]>([]);
  const expobj = new Export(
    lang.get("appointments", ["Appointments"]) +
      ", " +
      new Date().toDateString()
  );

  const CORE_FUNCTIONS = new coreFunctions();

  const handleSearch = (field: string, value: string) => {
    setAllSearch("");
    const existingAttribute = searchAttributes.find(
      (attr) => attr.key === field
    );

    if (existingAttribute) {
      setSearchAttributes(
        searchAttributes.map((attr) =>
          attr.key === field ? { ...attr, value } : attr
        )
      );
    } else {
      setSearchAttributes([
        ...searchAttributes,
        {
          key: field,
          value,
        },
      ]);
    }
  };

  const searchCols = (field: string, label: string) => {
    return {
      filterDropdown: (props: any) => {
        const { close } = props;

        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.focus();
          }
        }, 100);

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder={lang.get("appointments", [`Search ${label}`])}
              onChange={(e) => handleSearch(field, e.target.value)}
              value={
                searchAttributes.filter((item) => item.key === field)[0]?.value
              }
              style={{ marginBottom: 8, display: "block" }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  close();
                  if (actionRef.current) {
                    actionRef.current?.reset?.();
                    actionRef.current.reload();
                  }
                }
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                onClick={() => {
                  actionRef.current?.reset?.();
                  actionRef.current?.reload();
                }}
                style={{ width: 90 }}
              >
                {lang.get("appointments", ["Search"])}
              </Button>
              <Button
                onClick={() => close()}
                size="small"
                style={{ width: 90 }}
              >
                {lang.get("appointments", ["Close"])}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    };
  };

  const handleStatusChange = (appointment: string, status: string) => {
    const API_REQUEST = new apiRequest("appointment/change_status", true);
    API_REQUEST.send(
      {
        appointment,
        status,
      },
      (data: { status: string; message: string }) => {
        if (data.status === "success") {
          message.success(data.message);
          actionRef.current?.reload();
        } else {
          message.error(data.message);
        }
      }
    );
  };

  const handleDelete = (id: string) => {
    const API_REQUEST = new apiRequest("appointment/delete", true);
    API_REQUEST.send({ id }, (data: { status: string; message: string }) => {
      if (data.status === "success") {
        message.success(data.message);
        actionRef.current?.reload();
      } else {
        message.error(data.message);
      }
    });
  };

  useEffect(() => {
    CORE_FUNCTIONS.searchOptions(
      "",
      "appointments",
      "statuses",
      setStatusFilter
    );
  }, []);

  const columns: ProColumns<Appointment>[] = [
    {
      title: "#",
      dataIndex: "#",
      key: "#",
      sorter: false,
      render: (_, record: Appointment, index) => <Text>{index + 1}</Text>,
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      ...searchCols(
        "customer_name",
        lang.get("appointments", ["Customer Name"])
      ),
      render: (_, record: Appointment) => record.customer.label,
    },
    {
      title: "Vehicle Number",
      dataIndex: "vehicle_number",
      key: "vehicle_number",
      sorter: true,
      ...searchCols(
        "vehicle_number",
        lang.get("appointments", ["Vehicle Number"])
      ),
      render: (_, record: Appointment) => <Text>{record.vehicle.label}</Text>,
    },
    {
      title: "Appointment Date",
      dataIndex: "appointment_date",
      key: "appointment_date",
      sorter: true,
      ...searchCols(
        "appointment_date",
        lang.get("appointments", ["Appointment Date"])
      ),
      render: (_, record: Appointment) => (
        <Text>{record.appointment_date}</Text>
      ),
    },
    {
      title: "Service Category",
      dataIndex: "service_category",
      key: "service_category",
      sorter: true,
      ...searchCols(
        "service_category",
        lang.get("appointments", ["Service Category"])
      ),
      render: (_, record: Appointment) => (
        <Text>{record.service_category.label}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record: Appointment) => (
        <Select
          style={{ width: 150, background: "none" }}
          bordered={false}
          onChange={(e) => handleStatusChange(record.id, e)}
          value={record.status.value}
        >
          {statusFilter.map((opt) => (
            <Select.Option key={opt.id} value={opt.id}>
              {coreFunctions.renderIcon(opt.icon)}{" "}
              <span style={{ marginLeft: 8, color: opt.color }}>
                {opt.name}
              </span>
            </Select.Option>
          ))}
        </Select>
      ),
      sorter: true,
      ...searchCols("status", lang.get("appointments", ["Status"])),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      sorter: false,
      render: (_, record: Appointment) => (
        <>
          {" "}
          <Popconfirm
            title={lang.get("jobs", [
              "Are you sure you want to delete this record?",
            ])}
            onConfirm={() => handleDelete(record.id)}
          >
            <DeleteOutlined style={{ color: "red" }} />
          </Popconfirm>
        </>
      ),
    },
  ];

  const exportMenu: MenuProps["items"] = [
    {
      key: "csv",
      label: lang.get("appointments", ["Export CSV"]),
      onClick: () => {
        const csvData = tableData.map((r) => ({
          [lang.get("appointments", ["Customer Name"])]: r.customer.label,
          [lang.get("appointments", ["Vehicle Number"])]: r.vehicle.label,
          [lang.get("appointments", ["Appointment Date"])]: r.appointment_date,
          [lang.get("appointments", ["Service Category"])]:
            r.service_category.label,
          [lang.get("appointments", ["Status"])]: r.status.label,
        }));
        expobj.exportToCSV(csvData);
      },
    },
    {
      key: "excel",
      label: lang.get("appointments", ["Export Excel"]),
      onClick: () => {
        const excelData = tableData.map((r) => ({
          [lang.get("appointments", ["Customer Name"])]: r.customer.label,
          [lang.get("appointments", ["Vehicle Number"])]: r.vehicle.label,
          [lang.get("appointments", ["Appointment Date"])]: r.appointment_date,
          [lang.get("appointments", ["Service Category"])]:
            r.service_category.label,
          [lang.get("appointments", ["Status"])]: r.status.label,
        }));
        expobj.exportToExcel(excelData);
      },
    },
    {
      key: "pdf",
      label: lang.get("appointments", ["Export PDF"]),
      onClick: () => {
        expobj.exportToPDF({
          head: [
            [
              lang.get("appointments", ["Customer Name"]),
              lang.get("appointments", ["Vehicle Number"]),
              lang.get("appointments", ["Appointment Date"]),
              lang.get("appointments", ["Service Category"]),
              lang.get("appointments", ["Status"]),
            ],
          ],
          body: tableData.map((r) => [
            r.customer.label,
            r.vehicle.label,
            r.appointment_date,
            r.service_category,
            r.status.label,
          ]),
        });
      },
    },
  ];

  useEffect(() => {
    CORE_FUNCTIONS.searchOptions(
      "",
      "appointment",
      "statuses",
      setStatusFilter
    );
  }, []);

  useEffect(() => {
    actionRef.current?.reload();
  }, [statuses]);

  return (
    <>
      <ProTable
        columns={columns}
        actionRef={actionRef}
        dataSource={tableData}
        rowKey="key"
        pagination={{
          defaultCurrent: 1,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          onShowSizeChange: (current, size) => setPageSize(size),
        }}
        request={async (params, sort, filter) => {
          await new coreFunctions().waitRequest(10);

          const processedFilters = Object.entries(filter || {}).reduce(
            (acc, [key, value]) => {
              if (value && value.length > 0) {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, (string | number)[]>
          );

          const payload = {
            other_filters: filters,
            params: params,
            sorts: sort,
            filters: {
              ...processedFilters,
              status: statuses,
            },
            searches: searchAttributes,
            all_search: allSearch,
          };

          const API_REQUEST = new apiRequest("appointments/get", true);
          const response = await API_REQUEST.send(payload);

          if (response && response.status === "success") {
            const fetchedData = response.data || [];
            setTableData(fetchedData);
            setTotalRecords(response.total);

            return {
              data: fetchedData,
              total: totalRecords,
              success: true,
            };
          }

          return {
            data: [],
            total: 0,
            success: false,
          };
        }}
        search={false}
        scroll={{ x: 900 }}
        toolBarRender={() => [
          <Space key="toolbar" size="middle" wrap>
            <RangePicker
              key="date"
              onChange={(value) => {
                const dateRange = value || [];
                const [startDate, endDate] = dateRange;

                if (startDate && endDate) {
                  setFilters((prev) => [
                    ...prev.filter((f) => f.key !== "appointment_date"),
                    {
                      key: "appointment_date",
                      value: [
                        startDate.format("YYYY-MM-DD").toString(),
                        endDate.format("YYYY-MM-DD").toString(),
                      ],
                      type: "date_range",
                    },
                  ]);
                  actionRef?.current?.reload();
                }
              }}
            />
            <Input
              placeholder={lang.get("appointments", ["Global Search"])}
              allowClear
              value={allSearch}
              onChange={(e) => {
                setAllSearch(e.target.value);
                setSearchAttributes([]);
                actionRef.current?.reload();
              }}
            />
            <Select
              placeholder={lang.get("appointments", ["Status"])}
              style={{ width: "200px" }}
              showSearch
              onChange={(value) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.key !== "status_id"),
                  {
                    key: "status_id",
                    value,
                    type: "encrypt",
                  },
                ]);
                actionRef?.current?.reload();
              }}
              filterOption={false}
              onSearch={(q) =>
                CORE_FUNCTIONS.searchOptions(
                  q,
                  "appointment",
                  "statuses",
                  setStatusFilter
                )
              }
              onClick={() =>
                CORE_FUNCTIONS.searchOptions(
                  "",
                  "appointment",
                  "statuses",
                  setStatusFilter
                )
              }
              options={statusFilter.map((s) => ({
                label: s.name,
                value: s.id,
              }))}
            />
            <Dropdown.Button menu={{ items: exportMenu }} type="primary">
              {lang.get("appointments", ["Export"])}
            </Dropdown.Button>
          </Space>,
        ]}
      />
    </>
  );
};

export default AppointmentTable;
